import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/ui/Modal/Modal";
import Button from "@/ui/Button/Button";
import { RaffleModalCloseIcon } from "./icons";
import { formatMoney, RUBLE_SIGN } from "./utils";
import cls from "./styles.module.scss";

const SECTOR_PRIZES = [1000, 5000, 1000, 1000, 1000];

type WonPrizeModalProps = {
  prizeAmount: number | null;
  rides: number;
  winProbability: number | null;
  isOpen: boolean;
  isClaiming: boolean;
  onClose: () => void;
  onClaimPrize: () => void;
};

const formatWinProbability = (probability: number | null) => {
  if (probability === null) return "0%";

  return `${Math.round(probability * 100)}%`;
};

const WonPrizeStatCard = ({
  icon,
  value,
  label,
}: {
  icon: string;
  value: string | number;
  label: string;
}) => (
  <div className={cls.wonPrizeStatCard}>
    <img className={cls.wonPrizeStatIcon} src={icon} alt="" />
    <div className={cls.wonPrizeStatCopy}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  </div>
);

export const WonPrizeModal = ({
  prizeAmount,
  rides,
  winProbability,
  isOpen,
  isClaiming,
  onClose,
  onClaimPrize,
}: WonPrizeModalProps) => {
  const { t } = useTranslation();

  if (prizeAmount === null) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideHeader
      wrapperClassName={cls.wonPrizeModalWrapper}
      wrapperClosingClassName={cls.wonPrizeModalClosing}
      contentClassName={cls.wonPrizeModalContent}
      closeAnimationMs={320}
    >
      <div className={cls.wonPrizeRoot}>
        <img
          className={cls.wonPrizeBackground}
          src="/goals/you_won_bg.webp"
          alt=""
        />
        <button
          type="button"
          className={cls.wonPrizeClose}
          onClick={onClose}
          aria-label={t("raffle.info.close")}
        >
          <RaffleModalCloseIcon />
        </button>
        <div className={cls.wonPrizeWheelWrap} aria-hidden="true">
          <img
            className={cls.wonPrizeWheel}
            src="/goals/you_won_wheel.webp"
            alt=""
          />
          {[prizeAmount, ...SECTOR_PRIZES].map((amount, index) => (
            <div
              key={index}
              className={cls.wonPrizeWheelText}
              style={
                {
                  "--sector-index": index,
                } as React.CSSProperties
              }
            >
              <span>{t("raffle.won.wheelPrize")}</span>
              <strong>
                {formatMoney(amount)} {RUBLE_SIGN}
              </strong>
            </div>
          ))}
        </div>
        <div className={cls.wonPrizeCopy}>
          <strong className={cls.wonPrizeAmount}>
            +{formatMoney(prizeAmount)} {RUBLE_SIGN}
          </strong>
          <p className={cls.wonPrizeMessage}>{t("raffle.won.message")}</p>
          <div className={cls.wonPrizeStats}>
            <WonPrizeStatCard
              icon="/goals/growth_icon.png"
              value={rides}
              label={t("raffle.won.completedOrders")}
            />
            <WonPrizeStatCard
              icon="/goals/dice_icon.png"
              value={formatWinProbability(winProbability)}
              label={t("raffle.won.winChance")}
            />
          </div>
          <Button
            className={cls.wonPrizeClaimButton}
            label={t("raffle.won.claimPrize")}
            loading={isClaiming}
            onClick={onClaimPrize}
          />
        </div>
      </div>
    </Modal>
  );
};
